import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { PublicPageData } from '../lib/publicPage';

export interface Award {
  id: string;
  slug: string;
  language: string;
  title: string;
  description: string;
  image_url?: string;
  year?: string;
  sort_order?: number;
  focal_point?: number;
  published: boolean;
}

export interface Article {
  id: string;
  slug: string;
  language: string;
  title: string;
  date: string;
  description: string;
  content: string;
  published: boolean;
  created_at?: string;
}

export interface MediaMention {
  id: string;
  publisher_bg: string;
  publisher_en: string;
  title_bg: string;
  title_en: string;
  summary_bg: string;
  summary_en: string;
  source_url: string;
  logo_path: string;
  article_bg_slug: string;
  article_en_slug: string;
  sort_order: number;
  published: boolean;
}

type Language = 'bg' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  articles: Article[];
  awards: Award[];
  mediaMentions: MediaMention[];
  loading: boolean;
  refetch: () => void;
}

const translations = {
  bg: {
    // Navigation
    nav: {
      home: 'Начало',
      about: 'За нас',
      practice: 'Области на практика',
      notableCases: 'Знакови дела',
      awards: 'Награди',
      insights: 'Блог',
      faq: 'FAQ',
      contact: 'Контакти',
      consultation: 'Консултация',
      navFirstLine: 'АДВОКАТСКА КАНТОРА',
      navSecondLine: 'ПЕТЪР НИКОЛОВ'
    },
    // Hero Section
    hero: {
      established: 'ОСНОВАНА 2015',
      title: 'Силна правна защита в най-важните лични, имотни и финансови спорове',
      subtitle: 'Семейно право • Родителски права • Имотни спорове • Защита срещу кредитори',
      description: 'Специализирана правна помощ по семейни дела, родителски права, вещно право и имотни спорове, както и защита срещу кредитори, банки и ЧСИ. Представляваме клиенти във Варна, София и цяла България.',
      experience: 'Години опит',
      cases: 'Спечелени дела',
      freeConsultation: 'ЗАЯВИ КОНСУЛТАЦИЯ',
      ourServices: 'Нашите услуги',
      commitment: 'Моят ангажимент',
      commitmentText: 'Не обещавам лесни отговори. Обещавам ясни стъпки и честна комуникация от първия ден.',
      signature: '— Петър Николов',
      familyLaw: 'Семейно право',
      personalInjury: 'Лични вреди'
    },
    // About Section
    about: {
      badge: 'ЗА КАНТОРАТА',
      title: 'Различни правни направления. Един силен екип.',
      subtitle: 'Адвокатска кантора "Петър Николов" обединява адвокати и специалисти за правна защита по семейни, имотни, потребителски и административни въпроси.',
      name: 'Адвокат Петър Николов',
      logoAlt: 'Лого на адвокатска кантора "Петър Николов"',
      foundingPartner: 'Учредител на кантората',
      years: '10+ години',
      firmLocation: 'ВАРНА · СОФИЯ · ЦЯЛА БЪЛГАРИЯ',
      teamStatement: 'Зад всяко дело стои не само адвокат, а организиран екип - с правна стратегия, съдебно представителство и координация на работата по случая.',
      teamDetail: 'Когато един казус засяга различни области на правото, разглеждаме въпросите заедно като отбор, а Вие получавате обща посока на защита вместо отделни, несвързани решения.',
      practiceIndexTitle: 'Експертиза в ключовите области на правото',
      practiceIndexFamily: 'Защита на родителски права',
      practiceIndexProperty: 'Вещно право и имотни спорове',
      practiceIndexDebt: 'Защита при спорове с банки, кредитори и ЧСИ',
      practiceIndexAdmin: 'Правна помощ при административни спорове',
      teamStrategy: 'Правна стратегия',
      teamRepresentation: 'Съдебно представителство',
      teamCoordination: 'Координация на делото',
      practiceCta: 'Разгледайте практиката ни',
      evidenceCta: 'Вижте отличията и делата',
      portraitAlt: 'Адвокат Петър Николов, учредител на кантората',
      founderEyebrow: 'УЧРЕДИТЕЛ НА КАНТОРАТА',
      founderIntro: 'Професионалното признание е свързано с конкретна работа и конкретни резултати.',
      evidenceIntro: 'Признание, подкрепено с дела',
      description1: 'Адвокат Петър Николов е носител на приза "Юрист на годината" за 2017 г. Той е сред малкото юристи, постигнали пробив срещу държавата в дело за достъп до договорите за златните концесии, както и срещу голяма международна банка, която неправомерно е усвоявала средства по жилищен кредит.',
      practiceFocus: 'Кантората специализира в дела с висок личен и обществен залог - връщане на деца, неправомерно отнети от социалните служби, защита на семейства пред частни съдебни изпълнители и банки, спорове с фирми за бързи кредити, енерго и ВиК дружества, застрахователи и общини.',
      description2: 'Практиката на кантората обхваща семейни и имотни спорове, защита на длъжници и потребители и дела за достъп до обществена информация. По-долу представяме конкретни случаи и резултатите, описани в публични публикации.',
      description3: 'От години адвокат Николов води и обществени каузи - участва активно в родителския протест срещу Националната стратегия за детето и Истанбулската конвенция, като председателства родителското сдружение РОД и защитава интересите на българските семейства пред институциите. Неговата кантора във Варна е известна с про боно делата за самотни родители и семейства в крайна бедност, както и с благотворителни кампании за осигуряване на дом и нормален живот за нуждаещи се деца.',
      publicCauses: 'Обществени каузи и про боно',
      founded: 'Основана',
      successRate: 'Успеваемост',
      availability: '24/7',
      values: 'Наши ценности',
      integrity: 'Почтеност',
      integrityDesc: 'Непреклонни етични стандарти във всички взаимоотношения',
      excellence: 'Съвършенство',
      excellenceDesc: 'Преследване на най-високите правни стандарти',
      dedication: 'Отдаденост',
      dedicationDesc: 'Пълна отдаденост на успеха на клиента',
      compassion: 'Съчувствие',
      compassionDesc: 'Разбиране и съпричастен подход',
      credentials: 'Квалификации',
      sofiaUniversity: 'Русенски университет "Ангел Кънчев"',
      barAssociation: 'Член на Адвокатска колегия - Варна',
      lawyerOfTheYear: '"Юрист на годината" 2017',
      familyLawSpecialist: 'Специалист по семейно право и бракоразводни дела',
      personalInjuryExpert: 'Експерт по защита от ЧСИ, банки и колекторски фирми'
    },
    media: {
      badge: 'В МЕДИИТЕ',
      heroBadge: 'ОТЛИЧИЯ И МЕДИЙНИ УЧАСТИЯ',
      readArticle: 'Прочетете материала',
      watchReport: 'Вижте оригиналния репортаж'
    },
    // Practice Areas
    practice: {
      badge: 'ОБЛАСТИ НА ПРАКТИКА',
      title: 'Експертиза в ключови правни области',
      subtitle: 'От връщане на деца до битки с монополисти',
      // Child Protection
      childProtectionTitle: 'Връщане на деца и семейни права',
      childProtection: 'Защита на семейства',
      childProtectionDesc: 'Специализирано представителство при дела за връщане на деца, неправомерно отнети от социалните служби. Повече от 30 деца са върнати при семействата си благодарение на нашата упорита работа.',
      childReturn: 'Връщане на отнети деца',
      parentalRights: 'Защита на родителски права',
      socialServicesDefense: 'Защита срещу социални служби',
      divorce: 'Бракоразводни дела',
      // Debtor Protection
      debtorProtectionTitle: 'Защита на длъжници и потребители',
      debtorProtection: 'Срещу банки и монополисти',
      debtorProtectionDesc: 'Агресивна защита на длъжници срещу частни съдебни изпълнители, банки, колекторски фирми и монополисти. Постигаме пробиви, които връщат справедливостта и достойнството на хората.',
      bailiffDefense: 'Защита пред ЧСИ',
      bankDisputes: 'Спорове с банки',
      fastLoans: 'Фирми за бързи кредити',
      monopolyDefense: 'Битки с монополисти',
      propertyLawTitle: 'Вещно право и недвижими имоти',
      propertyLaw: 'Защита на собствеността',
      propertyLawDesc: 'Правна помощ при спорове за собственост и владение, придобивна давност, нотариални актове, делба, кадастър, сделки с имоти и защита от имотни измами.',
      ownershipDisputes: 'Спорове за собственост',
      adversePossession: 'Придобивна давност',
      titleDeeds: 'Оспорване на нотариални актове',
      propertyFraud: 'Защита от имотни измами',
      // Administrative
      administrativeTitle: 'Административно право',
      administrative: 'Срещу държавата',
      administrativeDesc: 'Смели дела срещу държавни институции, включително пробив срещу Министерския съвет за достъп до договорите за златни концесии. Защитаваме правото на информация и прозрачност.',
      stateInstitutions: 'Дела срещу държавни институции',
      publicAccess: 'Достъп до обществена информация',
      municipalities: 'Спорове с общини',
      utilities: 'Енерго и ВиК дружества',
      // Pro Bono
      proBonoTitle: 'Про боно и обществена дейност',
      proBono: 'Безвъзмездна помощ',
      proBonoDesc: 'Активно участие в обществени каузи - председател на сдружение РОД, защита на българските семейства. Про боно дела за самотни родители и семейства в крайна бедност.',
      singleParents: 'Самотни родители',
      povertyFamilies: 'Семейства в крайна бедност',
      rodAssociation: 'Сдружение РОД',
      charityCampaigns: 'Благотворителни кампании',
      consultationTitle: 'Готови ли сте за първа консултация?',
      consultationDesc: 'Свържете се с нас, за да обсъдим как можем да помогнем във вашия случай.',
      scheduleConsultation: 'Запазете консултация'
    },
    // Notable Cases
    notableCases: {
      badge: 'ЗНАКОВИ ДЕЛА',
      title: 'Реални случаи от нашата практика',
      subtitle: 'Казус, правна защита и резултат според публичните източници. Всеки нов случай се преценява самостоятелно.',
      problemLabel: 'Казус',
      defenseLabel: 'Защита',
      resultLabel: 'Резултат',
      sourceLabel: 'Публичен източник',
      case1: {
        title: 'Даниела Колева - Сливен',
        year: 'Отразено през 2018',
        category: 'Връщане на отнети деца',
        problem: 'Три деца са отделени от майка си и настанени извън семейството.',
        description: 'Адвокат Николов представлява майката в дело срещу Дирекция "Социално подпомагане" - Сливен.',
        outcome: 'Трите деца са върнати при майката според публикацията на "24 часа".',
        highlight: '3 деца върнати'
      },
      case2: {
        title: 'Златните концесии',
        year: '2014',
        category: 'Административно право',
        problem: 'Търси се достъп до договорите за добив на злато.',
        description: 'Адвокат Николов защитава Андрей Едрев по дело за достъп до договорите, описано в публикацията на Dunavmost.',
        outcome: 'Публикацията съобщава за успешно дело за достъп до договорите.',
        highlight: 'Достъп до информация'
      },
      case3: {
        title: 'Надвзети суми по жилищен кредит',
        year: '2016',
        category: 'Защита на потребители',
        problem: 'Младо семейство оспорва надвзети суми по жилищен кредит от "Уникредит".',
        description: 'Адвокат Николов представлява семейството в дело срещу банката, описано в публикацията на Dunavmost.',
        outcome: 'Според публикацията банката трябва да върне 12 500 лв. надвзети средства.',
        highlight: '12 500 лв.'
      },
      case4: {
        title: 'Деца от Балша и Търговище',
        year: 'Отразено през 2019',
        category: 'Защита на семейството',
        problem: 'Семейства търсят защита след отнемане на децата им от социалните служби.',
        description: 'В интервю за Дарик адвокат Николов описва работата си по случаите в Балша и Търговище и подготовката на искове срещу държавата.',
        outcome: 'Интервюто посочва, че родителите вече са върнали децата си в семейството.',
        highlight: 'Връщане в семейството'
      }
    },
    // Blog Section
    blog: {
      badge: 'БЛОГ',
      title: 'Статии и анализи',
      subtitle: 'Реални случаи, полезни съвети и важни уроци от нашата работа',
      article1Tag: 'Връщане на деца',
      article1Date: '5 април 2026',
      article1Title: 'Как Даниела от Сливен си върна трите деца от социалните служби',
      article1Desc: 'Историята на самотна майка, на която социалните служби отнемат децата заради бедност. След двегодишна битка, съдът постановява, че бедността не е основание за отнемане на деца.',
      article2Tag: 'Защита на длъжници',
      article2Date: '14 април 2026',
      article2Title: 'Как да се защитите от ЧСИ, банки и колекторски фирми',
      article2Desc: 'Всеки ден хиляди българи се сблъскват с агресивни колектори и неправомерни действия на частни съдебни изпълнители. Ето какво трябва да знаете за вашите права.',
      article3Tag: 'Административно право',
      article3Date: '22 април 2026',
      article3Title: 'Златните концесии: Как един адвокат победи държавата',
      article3Desc: 'Дело за достъп до обществена информация, което разкрива непрозрачното разпределение на златни концесии и създава прецедент за прозрачност в държавната администрация.',
      readArticle: 'Прочетете пълната статия →',
      viewAllArticles: 'Всички статии',
      loading: 'Зареждане на статията...',
      notFound: 'Статията не е намерена',
      article1Content: 'През 2016 година социалните служби в Сливен отнемат трите деца на Даниела Колева – самотна майка, отглеждаща децата си с минимални средства. Децата са разделени и настанени в различни приемни семейства. Даниела обжалва решението, но първата съдебна инстанция потвърждава отнемането.\n\nС нашата правна помощ случаят стига до Окръжен съд Сливен. След детайлен анализ на социалния доклад и икономическите данни, съдът приема нашия аргумент, че бедността сама по себе си не представлява основание за отнемане на деца съгласно българското законодателство и Конвенцията за правата на детето.\n\nПрез 2018 година Окръжният съд отменя решението на първата инстанция и постановява, че децата трябва незабавно да бъдат върнати при майка си. Това дело става основа за признаването на адвокат Петър Николов за "Юрист на годината" 2017 и показва, че системната правна защита може да поправи дори най-тежките несправедливости.',
      article2Content: 'Частните съдебни изпълнители и колекторските фирми често използват заплахи, тормоз и неправомерни действия срещу длъжници. Мнозина не знаят, че българското законодателство предоставя конкретни защити срещу такива практики.\n\nПърво, ЧСИ нямат право да конфискуват последното жилище на семейството, ако то е единствено. Второ, заплатата може да бъде запорирана само в размер до 1/3 от нетното ѝ възнаграждение при наличие на други приходи. Трето, всяка заплаха или публично оповестяване на задължения нарушава Закона за защита на личните данни.\n\nАко сте подложени на тормоз от колекторска фирма, важно е да документирате всяко обаждане, съобщение или посещение. С правната защита можете не само да спрете тормоза, но и да потърсите обезщетение за претърпените неимуществени вреди. Не оставяйте банките и колекторите да нарушават правата ви – законът е на ваша страна.',
      article3Content: 'През 2017 година подаваме искане по Закона за достъп до обществена информация към Министерския съвет относно разпределението на концесии за добив на злато в България. Държавната администрация отказва да предостави документите, позовавайки се на търговска тайна и национална сигурност.\n\nОбжалваме отказа пред Административния съд. В хода на делото демонстрираме, че разпределението на природни ресурси, собственост на българския народ, не може да бъде скрито зад оправдания за търговска тайна. Съдът постановява, че общественият интерес към прозрачността на управлението на държавните ресурси превишава търговския интерес на концесионерите.\n\nТова дело създава важен прецедент за бъдещи искания за достъп до информация и демонстрира, че един ангажиран адвокат може да принуди държавата да отчита действията си пред гражданите.'
    },
    // Contact Section
    contact: {
      badge: 'КОНТАКТИ',
      title: 'Свържете се с нас',
      subtitle: 'Направете първата стъпка към разрешаването на Вашия казус!',
      formTitle: 'Изпратете Запитване',
      name: 'Име',
      emailLabel: 'Имейл',
      phoneLabel: 'Телефон',
      subject: 'Тема',
      message: 'Съобщение',
      submitRequest: 'Изпрати Запитването',
      contactInfo: 'Контактна Информация',
      phone: '+359 882 744 951',
      email: 'kantora@advokatnikolov.com',
      varnaOffice: 'Офис Варна',
      varnaAddress: 'бул. Цар Освободител 44, ет.4, офис 6',
      sofiaOffice: 'Офис София',
      sofiaAddress: 'ул. Алабин 36, ет.2',
      officeHours: 'Работно време',
      hours: 'Понеделник - Петък: 9:00 - 18:00',
      emergencyTitle: 'Спешни Случаи',
      emergencyDesc: 'За спешни правни въпроси, достъпни 24/7:',
      selectSubject: 'Изберете тема',
      namePlaceholder: 'Въведете Вашето име',
      emailPlaceholder: 'Въведете Вашия имейл',
      phonePlaceholder: 'Въведете Вашия телефон',
      messagePlaceholder: 'Опишете Вашия случай...',
      familyLaw: 'Семейно право',
      personalInjury: 'Лични вреди',
      other: 'Друго',
      phoneNumber: 'Телефонен номер',
      selectPractice: 'Изберете област на практика',
      familyLawOption: 'Семейно право',
      otherLegal: 'Друг правен въпрос',
      describeMatter: 'Моля, опишете вашия правен въпрос...',
    },
    // Awards Section
    awards: {
      badge: 'НАГРАДИ',
      title: 'Награди и номинации',
      subtitle: 'Получени отличия и номинации, с посочени организатори и публични източници.',
      documentsTitle: 'Отличия и документи',
      awarded: 'НАГРАДА',
      nominated: 'НОМИНАЦИЯ',
      source: 'Публичен източник',
      prizeTitle: '"Юрист на годината" 2017',
      prizeOrganiser: '"Правен свят" и Международен правен център (ILAC)',
      prizeDescription: 'Приз за защитата на Даниела Колева и връщането на трите ѝ деца. Връчен през април 2018 г.',
      nomineeTitle: '"Адвокат на годината" 2021',
      nomineeOrganiser: 'Национални правосъдни награди - Балканска медийна асоциация',
      nomineeDescription: 'Номиниран в категорията "Адвокат на годината" на Националните правосъдни награди за 2021 г.'
    },
    // Footer
    footer: {
      description: 'Вашият доверен адвокат във Варна и София, предоставящ експертно представителство и непоколебима борба за справедливост.',
      quickLinks: 'Бързи връзки',
      services: 'Услуги',
      divorceCases: 'Разводни дела',
      childCustody: 'Попечителство на деца',
      copyright: '© 2026 AdvokatNikolov.com. Всички права запазени. | Обслужване на Варна, София и цяла България',
    },
    faq: {
      badge: 'ЧЕСТО ЗАДАВАНИ ВЪПРОСИ',
      title: 'Въпроси и отговори',
      subtitle: 'Отговори на най-честите въпроси относно правните ни услуги',
      q1: 'Колко струва първоначалната консултация?',
      a1: 'Първоначалната консултация е 130 евро. По време на срещата обсъждаме Вашия случай и преценяваме възможностите за правна защита. Хонорарът се определя индивидуално след запознаване с детайлите.',
      q2: 'Какви документи са необходими за бракоразводно дело?',
      a2: 'Основните документи включват: удостоверение за граждански брак, актове за раждане на деца (ако има), документи за собственост и доходи. При първата консултация ще Ви предоставим пълен списък според Вашата ситуация.',
      q3: 'Колко време отнема бракоразводно дело?',
      a3: 'Развод по взаимно съгласие обикновено приключва за 1-2 месеца. При исков развод срокът варира от 6 месеца до 2 години в зависимост от сложността на случая и натовареността на съда.',
      q4: 'Може ли ЧСИ да запорира цялата ми заплата?',
      a4: 'Не. Съгласно ГПК, несеквестируемият минимум е равен на минималната работна заплата. ЧСИ може да запорира само сумата над този минимум, а при наличие на деца на издръжка — защитата е още по-голяма.',
      q5: 'Обслужвате ли клиенти извън Варна и София?',
      a5: 'Да, обслужваме клиенти в цяла България. Провеждаме онлайн консултации и пътуваме до съответния съд при необходимост. Много от процедурите могат да бъдат извършени дистанционно.'
    }
  },
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About',
      practice: 'Practice Areas',
      notableCases: 'Landmark Cases',
      awards: 'Awards',
      insights: 'Blog',
      faq: 'FAQ',
      contact: 'Contact',
      consultation: 'Consultation',
      navFirstLine: 'PETAR NIKOLOV',
      navSecondLine: 'LAW FIRM'
    },
    // Hero Section
    hero: {
      established: 'ESTABLISHED 2015',
      title: 'Strong legal protection in critical family, property and financial disputes',
      subtitle: 'Family Law • Parental Rights • Property Disputes • Creditor Protection',
      description: 'Specialized legal assistance in family matters, parental rights, property law and real estate disputes, as well as protection against creditors, banks and bailiffs. Representing clients in Varna, Sofia and throughout Bulgaria.',
      experience: 'Years Experience',
      cases: 'Cases Won',
      freeConsultation: 'REQUEST CONSULTATION',
      ourServices: 'Our Services',
      commitment: 'My Commitment',
      commitmentText: 'I don\'t promise easy answers. I promise clear steps and honest communication from day one.',
      signature: '— Petar Nikolov',
      familyLaw: 'Family Law',
      personalInjury: 'Personal Injury'
    },
    // About Section
    about: {
      badge: 'ABOUT THE FIRM',
      title: 'Distinct Legal Disciplines. One Strong Team.',
      subtitle: 'Petar Nikolov Law Firm brings together lawyers and specialists to handle family, property, consumer and administrative matters.',
      name: 'Attorney Petar Nikolov',
      logoAlt: 'Petar Nikolov Law Firm logo',
      foundingPartner: 'Founding Partner',
      years: '10+ Years',
      firmLocation: 'VARNA · SOFIA · ACROSS BULGARIA',
      teamStatement: 'Behind each matter is more than an individual lawyer: a coordinated team providing legal strategy, court representation and case management.',
      teamDetail: 'When a matter spans several areas of law, we consider the issues together as a team, and you receive a coherent direction of defense instead of separate, disconnected solutions.',
      practiceIndexTitle: 'Expertise Across Key Areas of Law',
      practiceIndexFamily: 'Parental Rights Protection',
      practiceIndexProperty: 'Property Law and Real Estate Disputes',
      practiceIndexDebt: 'Protection in Disputes with Banks, Creditors and Bailiffs',
      practiceIndexAdmin: 'Legal Assistance in Administrative Disputes',
      teamStrategy: 'Legal Strategy',
      teamRepresentation: 'Court Representation',
      teamCoordination: 'Case Coordination',
      practiceCta: 'Explore Our Practice',
      evidenceCta: 'Explore Recognition and Cases',
      portraitAlt: 'Attorney Petar Nikolov, founder of the firm',
      founderEyebrow: 'FOUNDER OF THE FIRM',
      founderIntro: 'Professional recognition is grounded in specific work and tangible outcomes.',
      evidenceIntro: 'Recognition Backed by Casework',
      description1: 'Attorney Petar Nikolov received the "Jurist of the Year" award for 2017. He is among the few lawyers to have achieved a breakthrough against the state in a case for access to the gold concession contracts, as well as against a major international bank that had unlawfully appropriated funds from a mortgage loan.',
      practiceFocus: 'The firm specializes in cases with high personal and public stakes - the return of children unlawfully removed by social services, protecting families from private enforcement agents and banks, and disputes with payday lenders, energy and water utilities, insurers and municipalities.',
      description2: 'The firm handles family and property disputes, debtor and consumer protection, and access-to-information cases. Below are specific matters and outcomes described in public reports.',
      description3: 'For years, Attorney Nikolov has led public causes - actively participating in parental protests against the National Strategy for the Child and the Istanbul Convention, as president of the parental association ROD, defending the interests of Bulgarian families before institutions. His Varna office is known for pro bono cases for single parents and families in extreme poverty, as well as charitable campaigns to provide homes and normal lives for children in need.',
      publicCauses: 'Public Causes and Pro Bono',
      founded: 'Founded',
      successRate: 'Success Rate',
      availability: '24/7',
      values: 'Our Values',
      integrity: 'Integrity',
      integrityDesc: 'Uncompromising ethical standards in all dealings',
      excellence: 'Excellence',
      excellenceDesc: 'Pursuit of the highest legal standards',
      dedication: 'Dedication',
      dedicationDesc: 'Complete commitment to client success',
      compassion: 'Compassion',
      compassionDesc: 'Understanding and empathetic approach',
      credentials: 'Qualifications',
      sofiaUniversity: 'University of Ruse "Angel Kanchev"',
      barAssociation: 'Member of the Varna Bar Association',
      lawyerOfTheYear: '"Jurist of the Year" 2017',
      familyLawSpecialist: 'Specialist in Family Law and Divorce Proceedings',
      personalInjuryExpert: 'Expert in Defense Against Private Bailiffs, Banks, and Collection Agencies'
    },
    media: {
      badge: 'IN THE MEDIA',
      heroBadge: 'RECOGNITION AND MEDIA',
      readArticle: 'Read our article',
      watchReport: 'Visit the original report'
    },
    // Practice Areas
    practice: {
      badge: 'PRACTICE AREAS',
      title: 'Expertise in Key Legal Areas',
      subtitle: 'From reuniting families to challenging monopolies',
      // Child Protection
      childProtectionTitle: 'Child Return & Family Rights',
      childProtection: 'Family Protection',
      childProtectionDesc: 'Specialized representation in cases for returning children wrongfully taken by social services. Over 30 children have been reunited with their families thanks to our persistent work.',
      childReturn: 'Return of Removed Children',
      parentalRights: 'Protection of Parental Rights',
      socialServicesDefense: 'Defense Against Social Services',
      divorce: 'Divorce Proceedings',
      // Debtor Protection
      debtorProtectionTitle: 'Debtor & Consumer Protection',
      debtorProtection: 'Against Banks & Monopolies',
      debtorProtectionDesc: 'Aggressive defense of debtors against private bailiffs, banks, collection agencies, and monopolies. We achieve breakthroughs that restore justice and dignity to people.',
      bailiffDefense: 'Defense Against Private Bailiffs',
      bankDisputes: 'Bank Disputes',
      fastLoans: 'Fast Loan Companies',
      monopolyDefense: 'Battles with Monopolies',
      propertyLawTitle: 'Property Law and Real Estate',
      propertyLaw: 'Protection of Ownership',
      propertyLawDesc: 'Legal assistance with ownership and possession disputes, adverse possession, title deeds, partition, cadastral issues, real estate transactions and protection against property fraud.',
      ownershipDisputes: 'Ownership disputes',
      adversePossession: 'Adverse possession',
      titleDeeds: 'Challenges to title deeds',
      propertyFraud: 'Protection against property fraud',
      // Administrative
      administrativeTitle: 'Administrative Law',
      administrative: 'Against the State',
      administrativeDesc: 'Bold cases against state institutions, including a breakthrough against the Council of Ministers for access to gold concession contracts. We defend the right to information and transparency.',
      stateInstitutions: 'Cases Against State Institutions',
      publicAccess: 'Access to Public Information',
      municipalities: 'Municipality Disputes',
      utilities: 'Energy & Water Utilities',
      // Pro Bono
      proBonoTitle: 'Pro Bono & Social Advocacy',
      proBono: 'Free Legal Aid',
      proBonoDesc: 'Active participation in public causes - president of ROD association, defending Bulgarian families. Pro bono cases for single parents and families in extreme poverty.',
      singleParents: 'Single Parents',
      povertyFamilies: 'Families in Extreme Poverty',
      rodAssociation: 'ROD Association',
      charityCampaigns: 'Charity Campaigns',
      consultationTitle: 'Ready for Your First Consultation?',
      consultationDesc: 'Contact us to discuss how we can help with your case.',
      scheduleConsultation: 'Schedule a Consultation'
    },
    // Notable Cases
    notableCases: {
      badge: 'LANDMARK CASES',
      title: 'Real Cases from Our Practice',
      subtitle: 'The issue, representation and outcome as described in public sources. Each new matter is assessed individually.',
      problemLabel: 'Issue',
      defenseLabel: 'Representation',
      resultLabel: 'Outcome',
      sourceLabel: 'Public source',
      case1: {
        title: 'Daniela Koleva - Sliven',
        year: 'Reported in 2018',
        category: 'Reuniting Families',
        problem: 'Three children were removed from their mother and placed outside the family.',
        description: 'Attorney Nikolov represented the mother in a case against the Social Assistance Directorate in Sliven.',
        outcome: 'The three children were reunited with their mother, according to the report in "24 Chasa".',
        highlight: '3 children reunited'
      },
      case2: {
        title: 'Gold Concessions',
        year: '2014',
        category: 'Administrative Law',
        problem: 'Access was sought to gold-mining contracts.',
        description: 'Attorney Nikolov represented Andrey Edrev in an access-to-information case described by Dunavmost.',
        outcome: 'The report describes a successful case seeking access to the contracts.',
        highlight: 'Access to information'
      },
      case3: {
        title: 'Overcharged Mortgage Payments',
        year: '2016',
        category: 'Consumer Protection',
        problem: 'A young family disputed mortgage amounts overcharged by UniCredit.',
        description: 'Attorney Nikolov represented the family in a case against the bank described by Dunavmost.',
        outcome: 'According to the report, the bank was required to repay BGN 12,500 in overcharged funds.',
        highlight: 'BGN 12,500'
      },
      case4: {
        title: 'Children from Balsha and Targovishte',
        year: 'Reported in 2019',
        category: 'Family Protection',
        problem: 'Families sought legal assistance after social services removed their children.',
        description: 'In an interview with Darik, Attorney Nikolov described his work on cases in Balsha and Targovishte and the preparation of claims against the state.',
        outcome: 'The interview states that the parents had already been reunited with their children.',
        highlight: 'Family reunification'
      }
    },
    // Blog Section
    blog: {
      badge: 'BLOG',
      title: 'Articles & Analyses',
      subtitle: 'Real cases, helpful advice and important lessons from our work',
      article1Tag: 'Child Return',
      article1Date: 'April 5, 2026',
      article1Title: 'How Daniela from Sliven Got Her Three Children Back from Social Services',
      article1Desc: 'The story of a single mother whose children were taken by social services because of poverty. After a two-year battle, the court ruled that poverty is not grounds for removing children.',
      article2Tag: 'Debtor Defense',
      article2Date: 'April 14, 2026',
      article2Title: 'How to Defend Yourself Against Bailiffs, Banks and Collection Agencies',
      article2Desc: 'Every day thousands of Bulgarians face aggressive collectors and unlawful actions by private bailiffs. Here is what you need to know about your rights.',
      article3Tag: 'Administrative Law',
      article3Date: 'April 22, 2026',
      article3Title: 'The Gold Concessions: How One Attorney Beat the State',
      article3Desc: 'An access to public information case that revealed the non-transparent allocation of gold concessions and set a precedent for transparency in state administration.',
      readArticle: 'Read the full article →',
      viewAllArticles: 'All Articles',
      loading: 'Loading article...',
      notFound: 'Article not found',
      article1Content: 'In 2016, social services in Sliven removed the three children of Daniela Koleva – a single mother raising her children on minimal income. The children were separated and placed in different foster families. Daniela appealed the decision, but the first instance court confirmed the removal.\n\nWith our legal assistance, the case reached the Sliven District Court. After detailed analysis of the social report and economic data, the court accepted our argument that poverty alone does not constitute grounds for removing children under Bulgarian law and the Convention on the Rights of the Child.\n\nIn 2018, the District Court overturned the first-instance decision and ordered the children to be immediately returned to their mother. This case became the basis for recognizing Attorney Petar Nikolov as "Lawyer of the Year" 2017 and demonstrates that systematic legal defense can correct even the gravest injustices.',
      article2Content: 'Private bailiffs and collection agencies often use threats, harassment, and unlawful actions against debtors. Many do not know that Bulgarian law provides specific protections against such practices.\n\nFirst, bailiffs have no right to confiscate the family\'s last home if it is their only residence. Second, wages can be garnished only up to 1/3 of net earnings when other income exists. Third, any threat or public disclosure of debts violates the Personal Data Protection Act.\n\nIf you are subjected to harassment by a collection agency, it is important to document every call, message, or visit. With legal defense, you can not only stop the harassment but also seek compensation for suffered non-material damages. Do not let banks and collectors violate your rights – the law is on your side.',
      article3Content: 'In 2017, we submitted a request under the Access to Public Information Act to the Council of Ministers regarding the allocation of gold mining concessions in Bulgaria. The state administration refused to provide the documents, citing trade secrets and national security.\n\nWe appealed the refusal before the Administrative Court. During the proceedings, we demonstrated that the allocation of natural resources belonging to the Bulgarian people cannot be hidden behind trade secret justifications. The court ruled that the public interest in transparency of state resource management outweighs the commercial interests of concession holders.\n\nThis case created an important precedent for future information access requests and demonstrates that a committed attorney can force the state to account for its actions before the citizens.'
    },
    // Contact Section
    contact: {
      badge: 'CONTACT US',
      title: 'Get in Touch Today',
      subtitle: 'Take the first step toward resolving your legal matter with confidence!',
      formTitle: 'Send Us a Message',
      name: 'Full Name',
      emailLabel: 'Email',
      phoneLabel: 'Phone Number',
      subject: 'Subject',
      message: 'Message',
      submitRequest: 'Submit Request',
      contactInfo: 'Contact Information',
      phone: '+359 882 744 951',
      email: 'kantora@advokatnikolov.com',
      varnaOffice: 'Varna Office',
      varnaAddress: 'bul. "Tsar Osvoboditel" 44, floor 4, office 6',
      sofiaOffice: 'Sofia Office',
      sofiaAddress: 'ulitsa "Alabin" 36, floor 2',
      officeHours: 'Opening Hours',
      hours: 'Monday - Friday: 9:00 AM - 6:00 PM',
      emergencyTitle: 'Emergency Cases',
      emergencyDesc: 'For urgent legal matters, available 24/7:',
      selectSubject: 'Select a subject',
      namePlaceholder: 'Enter your full name',
      emailPlaceholder: 'Enter your email address',
      phonePlaceholder: 'Enter your phone number',
      messagePlaceholder: 'Describe your legal matter...',
      familyLaw: 'Family Law',
      personalInjury: 'Personal Injury',
      other: 'Other',
      phoneNumber: 'Phone Number',
      selectPractice: 'Select Practice Area',
      familyLawOption: 'Family Law',
      otherLegal: 'Other Legal Matter',
      describeMatter: 'Please describe your legal matter...'
    },
    // Awards Section
    awards: {
      badge: 'AWARDS',
      title: 'Awards and Nominations',
      subtitle: 'Awards received and nominations, with organisers and public sources.',
      documentsTitle: 'Recognition and Documents',
      awarded: 'AWARD',
      nominated: 'NOMINATION',
      source: 'Public source',
      prizeTitle: '"Jurist of the Year" 2017',
      prizeOrganiser: '"Legal World" and the International Legal Centre (ILAC)',
      prizeDescription: 'Award for representing Daniela Koleva and helping reunite her with her three children. Presented in April 2018.',
      nomineeTitle: '"Attorney of the Year" 2021',
      nomineeOrganiser: 'National Justice Awards - Balkan Media Association',
      nomineeDescription: 'Nominated in the "Attorney of the Year" category at the 2021 National Justice Awards.'
    },
    // Footer
    footer: {
      description: 'Your trusted attorney in Varna and Sofia, providing expert representation and unwavering fight for justice.',
      quickLinks: 'Quick Links',
      services: 'Services',
      divorceCases: 'Divorce Cases',
      childCustody: 'Child Custody',
      copyright: '© 2026 AdvokatNikolov.com. All rights reserved. | Serving Varna, Sofia, and throughout Bulgaria'
    },
    faq: {
      badge: 'FREQUENTLY ASKED QUESTIONS',
      title: 'Questions & Answers',
      subtitle: 'Answers to the most common questions about our legal services',
      q1: 'How much does the initial consultation cost?',
      a1: 'The initial consultation costs 130 euros. During the meeting we discuss your case and assess the options for legal protection. The fee is determined individually after reviewing the details.',
      q2: 'What documents are needed for a divorce case?',
      a2: 'Essential documents include: marriage certificate, birth certificates of children (if any), property ownership and income documents. During the first consultation, we will provide a complete list based on your situation.',
      q3: 'How long does a divorce case take?',
      a3: 'A mutual consent divorce typically concludes within 1-2 months. A contested divorce ranges from 6 months to 2 years depending on the complexity of the case and court workload.',
      q4: 'Can a bailiff seize my entire salary?',
      a4: 'No. Under the Civil Procedure Code, the non-seizable minimum equals the minimum wage. A bailiff can only seize amounts above this minimum, and if you have dependent children, the protection is even greater.',
      q5: 'Do you serve clients outside Varna and Sofia?',
      a5: 'Yes, we serve clients throughout Bulgaria. We conduct online consultations and travel to the relevant court when necessary. Many procedures can be handled remotely.'
    }
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
  initialData?: PublicPageData | null;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, initialData }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (initialData) return initialData.language;
    if (typeof window === 'undefined') return 'bg';
    const englishPracticeRoutes = ['/family-law-parental-rights', '/debtor-consumer-protection', '/property-law-real-estate', '/administrative-law'];
    const bulgarianPracticeRoutes = ['/semeino-pravo-roditelski-prava', '/zashtita-dlazhnitsi-potrebiteli', '/veshtno-pravo-nedvizhimi-imoti', '/administrativno-pravo'];
    if (englishPracticeRoutes.includes(window.location.pathname)) return 'en';
    if (bulgarianPracticeRoutes.includes(window.location.pathname)) return 'bg';
    try {
      const saved = window.localStorage.getItem('app-language');
      return (saved === 'bg' || saved === 'en') ? saved : 'bg';
    } catch {
      return 'bg';
    }
  });
  const [articles, setArticles] = useState<Article[]>(initialData?.articles ?? []);
  const [awards, setAwards] = useState<Award[]>(initialData?.awards ?? []);
  const [mediaMentions, setMediaMentions] = useState<MediaMention[]>(initialData?.mediaMentions ?? []);
  const [loading, setLoading] = useState(!initialData);

  const setLanguage = (lang: Language) => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('app-language', lang);
      } catch {
        setLanguageState(lang);
        return;
      }
    }
    setLanguageState(lang);
  };

  // Fetch articles and awards from Supabase
  const fetchArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching articles:', error);
        setLoading(false);
        return;
      }

      setArticles(data || []);

      const { data: awardsData, error: awardsError } = await supabase
        .from('awards')
        .select('*')
        .eq('published', true)
        .order('sort_order', { ascending: true });

      if (!awardsError) {
        console.log('Awards loaded:', awardsData?.length);
        setAwards(awardsData || []);
      }

      const { data: mediaData, error: mediaError } = await supabase
        .from('media_mentions')
        .select('*')
        .eq('published', true)
        .order('sort_order', { ascending: true });
      if (mediaError) console.error('Error fetching media mentions:', mediaError);
      else {
        const publishedSlugs = new Set((data || []).map(article => article.slug));
        setMediaMentions((mediaData || []).filter(mention => publishedSlugs.has(mention.article_bg_slug) && publishedSlugs.has(mention.article_en_slug)));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    window.addEventListener('focus', fetchArticles);
    return () => window.removeEventListener('focus', fetchArticles);
  }, []);

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[language];

    for (const k of keys) {
      value = value ? value[k] : undefined;
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, articles, awards, mediaMentions, loading, refetch: fetchArticles }}>
      {children}
    </LanguageContext.Provider>
  );
};
